import type { IInputOption } from 'components/form/input/types';
import { AssetImageV2 } from 'components/asset-image-v2';
import type { FindexAsset } from '@/types/models/asset';
import type { FindexInvestment } from '@/types/models/investment';
import { EAssetMaintainer, getRefId } from 'findef';
import { Grid } from 'components/grid';
import TinyBadge from 'components/tiny-badge';
import { prettyNumber } from '@/utils/number/format';
import { getMaintainedText } from '@/utils/asset/maintained';
import { useResponsive } from '~/composables/useResponsive';
import { toTitle } from '@/utils/string';

export const investmentToOption2 = (investment: FindexInvestment): IInputOption => {
  const id = getRefId(investment);
  const asset = investment.asset as FindexAsset;
  return {
    value: id,
    key: id,
    label: (investment.asset as FindexAsset)?.name,
    body: () => {
      return (
        <div class="w-full grid grid-cols-[max-content_max-content] items-center gap-4">
          <div
            style={{
              width: '1.6rem',
              aspectRatio: '1 / 1',
            }}
          >
            <AssetImageV2
              size="full"
              variant="gray"
              asset={investment.asset as FindexAsset}
              investment={investment as unknown as any}
              iconProps={{ size: "1rem" }}
            />
          </div>
          <div class="text-sm font-normal leading-normal text-gray-800">{asset?.name}</div>
        </div>
      );
    },
  };
};

export const investmentToOption4 = (investment: FindexInvestment): IInputOption => {
  const id = getRefId(investment);
  const asset = investment.asset as FindexAsset;
  const assetId = getRefId(asset);
  const responsive = useResponsive();

  const providerImport = investment?.providerImport;
  const provider =
    providerImport && typeof providerImport !== 'string' && 'provider' in (providerImport as any)
      ? (providerImport as any).provider
      : undefined;

	const assetTypeText = asset?.type ? toTitle(asset.type) : '';

  const investmentAmountText = prettyNumber(investment?.currentValue?.value || 0, {
    american: true,
    maxDecimals: 2,
    zeroIsInteger: true,
    suffix: investment?.currentValue?.type || '',
  });

  const maintained = getMaintainedText(asset, provider);
  const maintainedText =
    maintained.toLowerCase() === EAssetMaintainer.PROVIDER.toLowerCase()
      ? provider?.displayName || EAssetMaintainer.PROVIDER.toLowerCase()
      : maintained;

  return {
    value: investment,
    key: id || assetId,
    label: id || (investment.asset as FindexAsset)?.name,
    body: () => {
      return (
        <Grid
          class="w-full h-full"
          columns="max-content max-content"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid columns="max-content 1fr" alignItems="center" gap="0.625rem">
            {!responsive.isSmall.value &&
              <div
                class="flex justify-center items-center bg-gray-100 w-10 h-10 overflow-hidden"
                style={{ aspectRatio: '1 / 1' }}
              >
                <AssetImageV2
                  size="full"
                  variant="gray"
                  objectFit="cover"
                  asset={investment.asset as FindexAsset}
                  investment={investment as unknown as any}
                  iconProps={{ size: "1.25rem" }}
                />
              </div>
            }

            <div class="flex-col justify-start items-start inline-flex">
              <div class="text-[#111928] text-xs font-semibold ">{asset?.name}</div>
              <div class="text-gray-500 text-xs">{assetTypeText}</div>
            </div>
          </Grid>
          <Grid columns="max-content 1fr" alignItems="center" gap="0.625rem">
            <TinyBadge type="mltslct">
              <div class="text-center text-gray-500 text-xs font-semibold">
                {investmentAmountText}
              </div>
            </TinyBadge>
            {!responsive.isSmall.value &&
              <TinyBadge type="mltslct">
                <div class="flex items-center gap-1">
                  {provider?.iconUrl && (
                    <img
                      alt={provider?.name ?? 'provider'}
                      class="h-3.5 border border-gray-300 object-cover"
                      src={provider?.iconUrl}
                    />
                  )}
                  <div class="text-center text-gray-500 text-xs font-semibold font-['Inter'] leading-[18px] capitalize">
                    {maintainedText}
                  </div>
                </div>
              </TinyBadge>
            }
          </Grid>
        </Grid>
      );
    },
  };
};

export const investmentToOption3 = (investment: FindexInvestment): IInputOption => {
  const id = getRefId(investment);
  const asset = investment.asset as FindexAsset;
  const assetId = getRefId(asset);
  return {
    value: asset,
    key: assetId || id,
    label: assetId || (investment.asset as FindexAsset)?.name,
    body: () => {
      return (
        <div class="w-full grid grid-cols-[max-content_max-content] items-center gap-4">
          <div
            style={{
              width: '1.6rem',
              aspectRatio: '1 / 1',
            }}
          >
            <AssetImageV2
              size="full"
              variant="gray"
              asset={investment.asset as FindexAsset}
              investment={investment as unknown as any}
              iconProps={{ size: "1rem" }}
            />
          </div>
          <div class="text-sm font-normal leading-normal text-gray-800">{asset?.name}</div>
        </div>
      );
    },
  };
};

export const investmentToOption = (investment: FindexInvestment): IInputOption => {
  const id = getRefId(investment);
  return {
    value: id,
    key: id,
    label: (investment.asset as FindexAsset)?.name,
    bodyLeft: () => {
      return (
        <div
          style={{
            width: '1.6rem',
            aspectRatio: '1 / 1',
          }}
        >
          <AssetImageV2
            size="full"
            variant="gray"
            asset={investment.asset as FindexAsset}
            investment={investment as unknown as any}
            iconProps={{ size: "1rem" }}
          />
        </div>
      );
    },
    textClass: 'text-base font-normal leading-normal',
  };
};
